import { useMemo } from 'react'

import { ApolloError } from '@apollo/client'
import { ToastPosition, useToast as useChakraToast } from '@chakra-ui/react'

import { Alert, DarkProvider } from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import { PageMessageToast } from '@/components/ui'
import { uuid } from '@/utils/helpers'

type ToastVariant = 'success' | 'error' | 'warning'

export interface UseToastsMethodsIProps {
  showSuccess: (message: string) => void
  showError: (message?: string | ApolloError) => void
  /**
   * Partial-outcome toast (e.g. "resolved 5, skipped 2"). Renders the dark
   * Alert warning variant under isRebrand; the legacy PageMessageToast has
   * no warning style, so it falls back to error styling there.
   */
  showWarning: (message: string) => void
}

export interface UseToastsOptionsIProps {
  toastPosition?: ToastPosition
  toastDuration?: number
  defaultErrorMessage?: string
  preventDuplicate?: boolean
  /** When true, renders toasts with dark package Alert components (rebrand). */
  isRebrand?: boolean
  /** Color scheme for rebrand toasts (pass useLightMode() from the caller). */
  colorScheme?: ColorScheme
}

export const useToasts = ({
  toastPosition = 'top',
  toastDuration = 2000,
  defaultErrorMessage = 'Something went wrong.',
  preventDuplicate = true,
  isRebrand = false,
  colorScheme = 'dark',
}: UseToastsOptionsIProps = {}): UseToastsMethodsIProps => {
  const toast = useChakraToast()
  const successId = useMemo(uuid, [])
  const errorId = useMemo(uuid, [])
  const warningId = useMemo(uuid, [])

  const renderToast = (message: string, variant: ToastVariant) => {
    if (isRebrand) {
      return (
        <DarkProvider>
          <Alert colorScheme={colorScheme} title={message} variant={variant} />
        </DarkProvider>
      )
    }
    return (
      <PageMessageToast
        message={message}
        show
        success={variant === 'success'}
      />
    )
  }

  const showToast = (message: string, variant: ToastVariant, id: string) => {
    if (!toast.isActive(id)) {
      toast({
        id: preventDuplicate ? id : null,
        position: toastPosition,
        duration: toastDuration,
        render: () => renderToast(message, variant),
      })
    }
  }

  const showSuccess = (message: string) => {
    showToast(message, 'success', successId)
  }

  const showError = (error?: string | ApolloError) => {
    if (typeof error === 'string') {
      showToast(error ?? defaultErrorMessage, 'error', errorId)
    } else {
      showToast(error?.message ?? defaultErrorMessage, 'error', errorId)
    }
  }

  const showWarning = (message: string) => {
    showToast(message, 'warning', warningId)
  }

  return { showSuccess, showError, showWarning }
}
